import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  LinearProgress,
  Box,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleTrash } from "@fortawesome/pro-solid-svg-icons";
import {
  collection,
  getDocs,
  query,
  where,
  writeBatch,
  doc,
} from "firebase/firestore";
import { db } from "../../config/firebase";
import ModalDialogTitle from "./ModalDialogTitle";

// Firestore batched writes cap at 500 operations — stay under that.
const BATCH_SIZE = 450;

/**
 * Destructive "delete every document in a collection" button, gated behind
 * a confirmation modal and showing live progress while it deletes.
 *
 * @param {string} collectionName - Firestore collection to clear.
 * @param {Array<{field:string, operator:string, value:any}>} [whereConstraints]
 *   Optional filter so only matching documents are deleted (e.g. Students
 *   vs. StudentArchive both point at TpStudents but should only clear their
 *   own filtered slice, not the whole collection).
 * @param {Function} [onCleared] - Called once deletion finishes successfully.
 * @param {boolean} [disabled] - e.g. tie to the page's canEdit permission.
 */
const ClearCollectionButton = ({
  collectionName,
  whereConstraints = [],
  onCleared,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [total, setTotal] = useState(0);
  const [deletedCount, setDeletedCount] = useState(0);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const handleOpen = () => {
    setOpen(true);
    setClearing(false);
    setProgress(0);
    setTotal(0);
    setDeletedCount(0);
    setDone(false);
    setError("");
  };

  const handleClose = () => {
    if (clearing) return; // Don't allow closing mid-delete.
    setOpen(false);
  };

  const handleConfirm = async () => {
    setClearing(true);
    setError("");
    try {
      const constraints = whereConstraints.map((c) =>
        where(c.field, c.operator, c.value),
      );
      const q = constraints.length
        ? query(collection(db, collectionName), ...constraints)
        : collection(db, collectionName);

      const snap = await getDocs(q);
      const docs = snap.docs;
      setTotal(docs.length);

      if (docs.length === 0) {
        setProgress(100);
        setDone(true);
        setClearing(false);
        onCleared?.();
        return;
      }

      let deleted = 0;
      for (let i = 0; i < docs.length; i += BATCH_SIZE) {
        const chunk = docs.slice(i, i + BATCH_SIZE);
        const batch = writeBatch(db);
        chunk.forEach((d) => batch.delete(doc(db, collectionName, d.id)));
        await batch.commit();
        deleted += chunk.length;
        setDeletedCount(deleted);
        setProgress(Math.round((deleted / docs.length) * 100));
      }

      setDone(true);
      onCleared?.();
    } catch (err) {
      console.error(`Error clearing collection ${collectionName}:`, err);
      setError(
        "Something went wrong while clearing this collection. Please try again.",
      );
    } finally {
      setClearing(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        color="error"
        disabled={disabled}
        onClick={handleOpen}
        startIcon={<FontAwesomeIcon icon={faCircleTrash} />}
        sx={{ color: "white" }}
      >
        Clear Collection
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <ModalDialogTitle onClose={handleClose}>
          Clear Collection
        </ModalDialogTitle>
        <DialogContent>
          {!done ? (
            <>
              <Typography sx={{ mb: clearing ? 2 : 0 }}>
                Are you sure you want to clear this collection? Please make
                sure you have exported the collection first.
              </Typography>
              {clearing && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress variant="determinate" value={progress} />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: "block" }}
                  >
                    Deleting {deletedCount} of {total}…
                  </Typography>
                </Box>
              )}
              {error && (
                <Typography color="error" sx={{ mt: 2 }}>
                  {error}
                </Typography>
              )}
            </>
          ) : (
            <Typography>
              Done — {deletedCount} document
              {deletedCount === 1 ? "" : "s"} deleted.
            </Typography>
          )}
        </DialogContent>
        {!done && (
          <DialogActions>
            <Button
              onClick={handleConfirm}
              color="error"
              variant="contained"
              disabled={clearing}
            >
              {clearing ? "Clearing…" : "I'm Sure"}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
};

export default ClearCollectionButton;
